import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { getConsent, setConsent } from "@/lib/consent";

/**
 * First-visit consent banner covering marketing / advertising cookies
 * (Meta Pixel, ChatGPT Ads Pixel, GA4). Essential cookies (cart, login)
 * are exempted per Indian DPDP Act §7(a) and EU ePrivacy §5(3), so the
 * banner only asks for the trackers the user can meaningfully decline.
 *
 * Stays hidden once the user has made a choice — the setting can be
 * changed later from the Privacy Policy footer link.
 */
const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Delay so it doesn't fight LCP on the homepage.
    const t = setTimeout(() => {
      if (!getConsent()) setVisible(true);
    }, 800);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  const accept = () => {
    setConsent("accepted");
    setVisible(false);
  };
  const reject = () => {
    setConsent("rejected");
    setVisible(false);
  };

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-[70] px-3 pb-3 md:px-6 md:pb-4 pointer-events-none"
      data-testid="cookie-consent-banner"
    >
      <div
        className="pointer-events-auto max-w-3xl mx-auto bg-white border border-black/10 shadow-xl rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
        role="dialog"
        aria-label="Cookie consent"
      >
        <p className="text-[12.5px] text-neutral-700 leading-snug flex-1 min-w-0">
          <Cookie
            className="inline w-4 h-4 mr-1.5 -mt-0.5 text-neutral-500"
            strokeWidth={1.5}
          />
          We use cookies to run the site and to show you relevant Luxsova
          offers. You can opt out anytime.{" "}
          <Link
            to="/privacy"
            className="underline underline-offset-2 hover:opacity-70 whitespace-nowrap"
            data-testid="cookie-consent-privacy-link"
          >
            Privacy Policy
          </Link>
        </p>
        <div className="flex gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={reject}
            className="flex-1 sm:flex-none text-xs px-4 py-2.5 border border-black/20 hover:bg-neutral-50 rounded-lg"
            data-testid="cookie-consent-reject"
          >
            Opt out
          </button>
          <button
            type="button"
            onClick={accept}
            className="flex-1 sm:flex-none text-xs font-medium px-6 py-2.5 bg-black text-white hover:opacity-85 rounded-lg"
            data-testid="cookie-consent-accept"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
